import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import {
  Box,
  IconButton,
  Toolbar,
  useMediaQuery,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Avatar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const drawerWidth = 225;
const collapsedWidth = 55;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: "#ffffff",
  color: "#0f172a",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
  border: "none",
  borderBottom: "none",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: `calc(100% - ${collapsedWidth}px)`,
  marginLeft: collapsedWidth,
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface NavbarProps {
  open: boolean;
  onDrawerOpen: () => void;
  onDrawerClose: () => void;
  onLogoutClick: () => void;
}

const Navbar = ({ open, onDrawerOpen, onDrawerClose, onLogoutClick }: NavbarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const userString = localStorage.getItem("user");
  const user: any = userString ? JSON.parse(userString) : null;

  const activeUserName = user?.name || user?.userName || user?.driverName || "Admin User";
  const activeUserRole =
    user?.role === "superAdmin"
      ? "Administrator"
      : user?.role === "schoolAdmin"
      ? "School Admin"
      : "Administrator";

  return (
    <AppBar
      position="fixed"
      open={open}
      sx={isMobile ? { width: "100%", marginLeft: 0 } : {}}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", minHeight: 65 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            color="inherit"
            onClick={open ? onDrawerClose : onDrawerOpen}
          >
            <MenuIcon sx={{ color: "#0f172a", fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.2,
              py: 0.5,
              borderRadius: 2.5,
              cursor: "pointer",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:hover": {
                border: "none",
                outline: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
              },
              "&:focus": {
                border: "none",
                outline: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
              },
            }}
            onClick={onLogoutClick}
          >
            <Avatar sx={{ width: 30, height: 30, bgcolor: "#e2e8f0", color: "#0f172a", fontSize: 13, fontWeight: 600, border: "none", outline: "none", boxShadow: "none" }}>
              {activeUserName?.charAt(0) || "A"}
            </Avatar>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 85 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: "#0f172a", border: "none", outline: "none" }}>
                {activeUserName}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "#64748b", border: "none", outline: "none" }}>
                {activeUserRole}
              </Typography>
            </Box>
            <KeyboardArrowDownIcon sx={{ color: "#0f172a", fontSize: 16 }} />
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
